import {
    categoryDialogNameCategory,
    categoryDialogTextFieldLabel,
    categoryDialogColorPickerLabel,
    categoryDialogSave,
    categoryDialogCancel,
} from './CategoryDialog.locstring.json';
import loc from 'owa-localize';
import resetNewCategoryViewState from '../actions/newCategoryMenu/resetNewCategoryViewState';
import setColorPickerSelectedColorId from '../actions/newCategoryMenu/setColorPickerSelectedColorId';
import setNewCategoryText from '../actions/newCategoryMenu/setNewCategoryText';
import categoryStore from '../store/store';
import CategoryActionSource from '../utils/CategoryActionSource';
import { isCategoryNameValid } from '../utils/getCategoryNameErrorText';
import { observer } from 'mobx-react-lite';
import { ITextField } from 'office-ui-fabric-react/lib/components/TextField';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { Dialog, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { SwatchColorPicker } from 'office-ui-fabric-react/lib/SwatchColorPicker';
import toggleNewCategoryShouldFavorite from '../actions/newCategoryMenu/toggleNewCategoryShouldFavorite';
import StarCharm from 'owa-star-charm/lib/components/StarCharm';
import { addToFavoritesCategoryText } from 'owa-locstrings/lib/strings/addtofavoritescategorytext.locstring.json';
import { removeFromFavoritesCategoryText } from 'owa-locstrings/lib/strings/removefromfavoritescategorytext.locstring.json';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getColorCellMenuItems, getColorGridCellStyles } from '../utils/getColorPickerProps';

import * as React from 'react';

const styles = require('./NewCategoryMenuItem.scss');

export interface CategoryDialogProps {
    actionSource: CategoryActionSource;
    createNew: (
        name: string,
        selectedColorId: number,
        shouldFavorite: boolean,
        actionSource: CategoryActionSource
    ) => void;
    title: string;
}

export default observer(function CategoryDialog(props: CategoryDialogProps) {
    let textField: ITextField;

    const renderNewCategoryEditMenu = () => {
        const { newCategoryViewState } = categoryStore.categoryMenuViewState;
        // Only render if in a new category view state
        if (!newCategoryViewState) {
            return null;
        }
        return (
            <Dialog
                hidden={!newCategoryViewState}
                title={props.title}
                onDismiss={resetNewCategoryViewState}
                minWidth={'450px'}>
                <div className={styles.dialogSecondRow}>
                    {renderCategoryTextField()}
                    {renderToggleFavoriteButton()}
                </div>
                <div className={styles.colorLabel}>{loc(categoryDialogColorPickerLabel)}</div>
                {renderColorPickerControl(newCategoryViewState.selectedColorId)}
                <DialogFooter styles={{ actionsRight: styles.footerRight }}>
                    <PrimaryButton
                        disabled={!!newCategoryViewState.errorText}
                        onClick={commitNewCategory}
                        text={loc(categoryDialogSave)}
                    />
                    <DefaultButton
                        onClick={resetNewCategoryViewState}
                        text={loc(categoryDialogCancel)}
                    />
                </DialogFooter>
            </Dialog>
        );
    };

    const renderToggleFavoriteButton = () => {
        // We don't support add/remove favorites in explicit logon scenarios
        if (getUserConfiguration()?.SessionSettings?.IsExplicitLogon) {
            return null;
        }

        const addText = loc(addToFavoritesCategoryText);
        const removeText = loc(removeFromFavoritesCategoryText);
        const isSetToFavorite =
            categoryStore.categoryMenuViewState.newCategoryViewState.shouldFavorite;
        return (
            <StarCharm
                isStarred={isSetToFavorite}
                onClick={toggleNewCategoryShouldFavorite}
                iconStyles={styles.categoryActionIcon}
                ariaLabelText={isSetToFavorite ? removeText : addText}
                tooltip={{
                    starred: removeText,
                    unstarred: addText,
                }}
            />
        );
    };

    /**
     * Render the color picker control
     */
    const renderColorPickerControl = (categoryColorId: string): JSX.Element => {
        return (
            <SwatchColorPicker
                key={'category_color_picker'}
                cellShape={'circle'}
                colorCells={getColorCellMenuItems()}
                columnCount={10}
                cellHeight={30}
                cellWidth={30}
                cellBorderWidth={2}
                getColorGridCellStyles={getColorGridCellStyles}
                onColorChanged={setColorPickerSelectedColorId}
                selectedId={categoryColorId}
                shouldFocusCircularNavigate={true}
            />
        );
    };

    /**
     * Render the text field to input the new category name
     */
    const renderCategoryTextField = (): JSX.Element => {
        const {
            errorText,
            categoryText,
        } = categoryStore.categoryMenuViewState.newCategoryViewState;
        // Do not show any error message if user hasn't started typing
        const showErrorMsg = errorText && categoryText !== '';
        return (
            <TextField
                autoComplete={'new-password'}
                label={loc(categoryDialogTextFieldLabel)}
                autoFocus={true}
                componentRef={categoryTextFieldRefCallback}
                className={
                    showErrorMsg ? styles.newCategoryTextField : styles.newCategoryTextFieldNoError
                }
                multiline={false}
                onChange={onCategoryTextChanged}
                onKeyDown={onKeyDownOnNewTextField}
                placeholder={loc(categoryDialogNameCategory)}
                value={categoryText}
                errorMessage={showErrorMsg && errorText}
            />
        );
    };

    const categoryTextFieldRefCallback = (ref: ITextField) => {
        textField = ref;
    };

    /**
     * Store text
     */
    const onCategoryTextChanged = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        text: string
    ) => {
        setNewCategoryText(text);
    };

    /**
     * Handler key down event on the TextField
     * OnEnter- call commit new category (calls create from props)
     * OnEscape- resetNewCategoryViewState, which will dismiss *only* the new category dialog
     */
    const onKeyDownOnNewTextField = (event: React.KeyboardEvent<unknown>) => {
        switch (event.keyCode) {
            case KeyCodes.enter:
                // Stop propagation and default so other forms are not submitted
                event.stopPropagation();
                event.preventDefault();
                commitNewCategory();
                break;

            case KeyCodes.escape:
                // Stop propagation so that it does not dismiss the entire menu.
                event.stopPropagation();
                resetNewCategoryViewState();
                break;
        }
    };

    const commitNewCategory = () => {
        const categoryName: string = textField.value.trim();
        const colorId: number = +categoryStore.categoryMenuViewState.newCategoryViewState
            .selectedColorId;
        if (isCategoryNameValid(categoryName)) {
            props.createNew(
                categoryName,
                colorId,
                categoryStore.categoryMenuViewState.newCategoryViewState.shouldFavorite,
                props.actionSource
            );
            resetNewCategoryViewState();
        }
    };

    return renderNewCategoryEditMenu();
});
